import { Card, Badge } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { prsService } from '@/services/api';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';
import { FeedbackControl } from '@/common/components/feedback-control';

const STATUS_VARIANT: Record<string, string> = {
  OPEN: 'primary', MERGED: 'success', CLOSED: 'secondary',
};

/** Human-facing Bitbucket PR URL (same derivation as the list). */
function prWebUrl(p: { webUrl?: string; prUrl: string }): string {
  if (p.webUrl) return p.webUrl;
  return (p.prUrl || '')
    .replace('api.bitbucket.org/2.0/repositories', 'bitbucket.org')
    .replace('/pullrequests/', '/pull-requests/');
}

export default function PrDetail() {
  const [params] = useSearchParams();
  const prUrl = params.get('prUrl') ?? '';
  const { data, loading, error } = useAsync(() => prsService.getPr(prUrl), [prUrl]);

  return (
    <Card>
      <Card.Header>Pull request</Card.Header>
      <Card.Body>
        <AsyncBoundary loading={loading} error={error}>
          {data && (
            <>
              <dl className="row">
                <dt className="col-sm-3">PR</dt>
                <dd className="col-sm-9">
                  <a href={prWebUrl(data)} target="_blank" rel="noreferrer">
                    {data.bitbucketPrId ? `#${data.bitbucketPrId}` : data.prUrl}
                  </a>
                </dd>
                <dt className="col-sm-3">Repo</dt><dd className="col-sm-9">{data.repo}</dd>
                <dt className="col-sm-3">Type</dt><dd className="col-sm-9">{data.prType ?? '-'}</dd>
                <dt className="col-sm-3">Status</dt>
                <dd className="col-sm-9">
                  <Badge bg={STATUS_VARIANT[data.status ?? ''] ?? 'secondary'}>{data.status ?? '-'}</Badge>
                  {data.outcome ? ` (${data.outcome})` : ''}
                </dd>
                <dt className="col-sm-3">Validation</dt><dd className="col-sm-9">{data.validationStatus ?? '-'}</dd>
                <dt className="col-sm-3">CVEs</dt><dd className="col-sm-9">{(data.cveIds ?? []).join(', ') || '-'}</dd>
              </dl>

              <FeedbackControl
                subjectType="PR"
                subjectId={data.prUrl}
                repo={data.repo}
                submit={(subjectId, repo, actor, outcome, comment) =>
                  prsService.submitPrFeedback(subjectId, repo, actor, outcome, comment)}
                listFeedback={(subjectId) => prsService.listPrFeedback(subjectId)}
              />
            </>
          )}
        </AsyncBoundary>
      </Card.Body>
    </Card>
  );
}
